import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useState } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../hooks/react-redux';
import { CalculatorInput, CalculatorOptionType, CalculatorPadOptionDB, CalculatorPadOptionUI } from '../../admin/calculator/store';
import { getAllDropdownLists } from '../../admin/dropdown-lists/store';
import { Dropdown, DropdownOption } from '../dropdown/Dropdown';
import { Icon } from '../icon/Icon';
import { Tick } from '../icons';
import { Number } from '../number/Number';
import styles from './Calculator.module.scss';
import { LegacyCalculatorInputType } from './constants';
import { formatDate, FULL_MONTH_FORMAT } from '../../../utils/luxon';
import { DatePicker } from '../datepicker/DatePicker';

const { white } = styles;

interface CalculatorPadOptionProps {
    calculatorPadOption: CalculatorPadOptionDB | CalculatorPadOptionUI;
    disabled: boolean;
    addOption: (value: CalculatorInput) => void;
    inputWrapperWidth: string;
}

export const CalculatorPadOption: React.FC<CalculatorPadOptionProps> = ({
    calculatorPadOption,
    addOption,
    disabled,
    inputWrapperWidth
}) => {
    const [numberInput, setNumberInput] = useState<number | undefined>(undefined);
    const [decimalInput, setDecimalInput] = useState<number | undefined>(undefined);
    const [dateInput, setDateInput] = useState<Date | null>(null);
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const { type, label, config: { inputFontSize } } = calculatorPadOption;

    const getDropdownOptions = useCallback((dropdownName: string) => {
        if (dropdownName === 'LTST') {
            return [{ value: 'LT', label: 'LT' }, { value: 'ST', label: 'ST' }];
        }

        if (dropdownName === 'AffectedParty') {
            return [{ value: 'Party A', label: 'Party A' }, { value: 'Party B', label: 'Party B' }, { value: 'Both Parties', label: 'Both Parties' }];
        }

        const list = dropdownLists.find(({ name }) => name === dropdownName)?.options || [];
        return list.map(value => ({ value, label: value }));
    }, [dropdownLists]);

    const formatNumber = useCallback((number: number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','), []);

    const addNumberOption = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === 'enter' && !isUndefined(numberInput)) {
            addOption({ value: formatNumber(numberInput), label: 'Number', type: LegacyCalculatorInputType.NUMBER });
            setNumberInput(undefined);
        }
    }, [addOption, numberInput, setNumberInput, formatNumber]);

    const addDecimalOption = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === 'enter' && !isUndefined(decimalInput)) {
            addOption({ value: formatNumber(decimalInput), label: 'Decimal', type: LegacyCalculatorInputType.DECIMAL });
            setDecimalInput(undefined);
        }
    }, [addOption, decimalInput, setDecimalInput, formatNumber]);

    const addDateOption = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key.toLowerCase() === 'enter' && !isNull(dateInput)) {
            addOption({ value: formatDate(dateInput, FULL_MONTH_FORMAT), label: 'Date', type: LegacyCalculatorInputType.DATE });
            setDateInput(null);
        }
    }, [addOption, dateInput, setDateInput]);

    const addNumberOptionClick = useCallback(() => {
        if (!isUndefined(numberInput)) {
            addOption({ value: formatNumber(numberInput), label: 'Number', type: LegacyCalculatorInputType.NUMBER });
            setNumberInput(undefined);
        }
    }, [addOption, numberInput, setNumberInput, formatNumber]);

    const addDecimalOptionClick = useCallback(() => {
        if (!isUndefined(decimalInput)) {
            addOption({ value: formatNumber(decimalInput), label: 'Decimal', type: LegacyCalculatorInputType.DECIMAL });
            setDecimalInput(undefined);
        }
    }, [addOption, decimalInput, setDecimalInput, formatNumber]);

    const addDateOptionClick = useCallback(() => {
        if (!isNull(dateInput)) {
            addOption({ value: formatDate(dateInput, FULL_MONTH_FORMAT), label: 'Date', type: LegacyCalculatorInputType.DATE });
            setDateInput(null);
        }
    }, [addOption, dateInput, setDateInput]);

    const addDropdownOption = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null, label: string, type: string) => {
        if (!isNull(dropdownValue)) {
            let value = (dropdownValue as DropdownOption).value;
            if (type === LegacyCalculatorInputType.RATING_TYPE) {
                value = `${label} ${value}`;
            }
            if (type === LegacyCalculatorInputType.AFFECTED_PARTY) {
                const isBothParties = value === 'Both Parties';
                value = `${value} ${isBothParties ? 'are' : 'is'} Affected Part${isBothParties ? 'ies' : 'y'}`;
            }
            addOption({ value, label, type });
        }
    }, [addOption]);

    const addStandardOption = useCallback(({ type, label, inputType }: CalculatorPadOptionDB | CalculatorPadOptionUI) => {
        if (type === CalculatorOptionType.STANDARD) {
            const value = ([LegacyCalculatorInputType.RATING_CONDITIONAL, LegacyCalculatorInputType.OPERATIONAL_ADMINISTRATIVE_DEFAULT] as string[]).includes(inputType) ? `(${label})` : label;
            addOption({ value, label, type: inputType });
        }
        if (type === CalculatorOptionType.TEXT) {
            addOption({ value: '', label, type: inputType });
        }
    }, [addOption]);

    const getInputContent = useCallback((input: CalculatorPadOptionDB | CalculatorPadOptionUI, inputDisabled: boolean) => {
        switch (input.type) {
            case CalculatorOptionType.DROPDOWN: {
                const { config: { dropdownName }, inputType, label } = input as CalculatorPadOptionUI;
                const dropdownOptions = getDropdownOptions(dropdownName!);
                return <div className={styles.dropdownOption}>
                    <Dropdown
                        options={dropdownOptions}
                        value={null}
                        onChange={value => addDropdownOption(value, label, inputType)}
                        menuPortalTarget={document.body}
                        hideIndicator
                        maxControlHeight='30px'
                        minControlHeight='30px'
                        disabled={inputDisabled || disabled}
                        disableControlColor={white}
                    />
                </div>;
            }
            case CalculatorOptionType.NUMBER:
                return <div className={styles.numberOption}>
                    <Number
                        value={numberInput}
                        onChange={val => setNumberInput(val)}
                        onKeyDown={addNumberOption}
                        disabled={inputDisabled || disabled}
                        height='18px'
                    />
                    <div onClick={addNumberOptionClick} className={styles.numberButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.DECIMAL:
                return <div className={styles.numberOption}>
                    <Number
                        value={decimalInput}
                        onChange={val => setDecimalInput(val)}
                        onKeyDown={addDecimalOption}
                        disabled={inputDisabled || disabled}
                        allowDecimal
                        height='18px'
                    />
                    <div onClick={addDecimalOptionClick} className={styles.numberButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.DATE:
                return <div className={styles.dateOption}>
                    <DatePicker
                        value={dateInput}
                        onChange={val => setDateInput(val)}
                        onKeyDown={addDateOption}
                        disabled={inputDisabled || disabled}
                        testId={`calc-pad-options-${input.calculatorPadOptionId}`}
                        reducedHeight
                        placeholder='Select...'
                    />
                    <div onClick={addDateOptionClick} className={styles.dateButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.TEXT:
            case CalculatorOptionType.STANDARD:
            default:
                return null;
        }
    }, [getDropdownOptions, addDropdownOption, addNumberOption, addNumberOptionClick, setNumberInput, numberInput, addDecimalOption, addDecimalOptionClick, setDecimalInput, decimalInput, disabled, addDateOption, addDateOptionClick, dateInput]);

    const getCursorType = useCallback((type: CalculatorOptionType, disabled: boolean) => {
        if (disabled) {
            return 'not-allowed';
        }
        return [CalculatorOptionType.STANDARD, CalculatorOptionType.TEXT].includes(type) ? 'pointer' : 'default';
    }, []);

    return (
        <button className={styles.inputWrapper} onClick={() => addStandardOption(calculatorPadOption)} style={{ cursor: getCursorType(type, disabled), width: inputWrapperWidth }} disabled={disabled}>
            <div className={styles.inputLabel} style={{ fontSize: inputFontSize }}>{label}</div>
            {getInputContent(calculatorPadOption, disabled)}
        </button>
    );
};
